import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Bell, BellOff, CheckCheck, Loader2, AlertTriangle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import API from '../../lib/api';
import { Button } from '../../components/ui/button';
import { ErpHeading } from '../../components/erp/ErpShared';
import '../../components/erp/assets.css';

const SEV = { high: { cls: 'bg-rose-500/15 text-rose-300 border-rose-500/20', label: 'Urgent' }, medium: { cls: 'bg-amber-500/15 text-amber-300 border-amber-500/20', label: 'Atenție' }, low: { cls: 'bg-blue-500/15 text-blue-300 border-blue-500/20', label: 'Info' } };

export default function NotificationsPage() {
  const [data, setData] = useState(null);
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    API.get(`/erp/notifications/list${onlyUnread ? '?only_unread=true' : ''}`).then(r => setData(r.data)).catch(() => setData(false));
  }, [onlyUnread]);
  useEffect(() => { load(); }, [load]);

  const markRead = async keys => { setBusy(true); try { await API.post('/erp/notifications/read', { keys }); load(); } finally { setBusy(false); } };
  const markAll = async () => { setBusy(true); try { await API.post('/erp/notifications/read-all'); toast.success('Toate marcate ca citite'); load(); } finally { setBusy(false); } };

  if (data === null) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (data === false) return <p className="text-sm text-rose-400 p-6" data-testid="notif-error">Nu am putut încărca notificările.</p>;

  return <div data-testid="notifications-page">
    <ErpHeading title="Notificări operaționale">
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">{data.unread} necitite din {data.total}</span>
        <Button variant="outline" size="sm" onClick={markAll} disabled={busy || !data.unread} data-testid="notif-read-all"><CheckCheck className="w-4 h-4 mr-1" />Marchează toate</Button>
      </div>
    </ErpHeading>
    <p className="text-sm text-muted-foreground mb-4 max-w-2xl">Alerte calculate automat din datele ERP: documente și verificări expirate, termene de proiect apropiate, stoc sub minim și depășiri de buget.</p>
    <label className="flex items-center gap-2 text-sm text-muted-foreground mb-4"><input type="checkbox" checked={onlyUnread} onChange={e => setOnlyUnread(e.target.checked)} data-testid="notif-unread-filter" />Doar necitite</label>

    {!data.items.length ? <div className="border border-white/5 rounded-xl p-10 text-center text-muted-foreground" data-testid="notif-empty"><BellOff className="w-8 h-8 mx-auto mb-2 opacity-50" />Nicio notificare.</div>
      : <div className="space-y-2" data-testid="notif-list">
        {data.items.map(n => { const s = SEV[n.severity] || SEV.low;
          return <div key={n.key} className={`flex items-start gap-3 border rounded-xl p-3 ${n.read ? 'border-white/5 opacity-60' : 'border-white/10 bg-white/[0.02]'}`} data-testid={`notif-${n.key}`}>
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${s.cls}`}><AlertTriangle className="w-4 h-4" /></div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap"><span className="text-sm font-medium">{n.title}</span><span className={`text-[10px] px-2 py-0.5 rounded-full border ${s.cls}`}>{s.label}</span><span className="text-[10px] text-muted-foreground">{n.type}</span></div>
              <p className="text-sm text-muted-foreground mt-0.5">{n.message}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Link to={n.link}><Button variant="ghost" size="sm" data-testid={`notif-open-${n.key}`}>Deschide<ArrowRight className="w-3.5 h-3.5 ml-1" /></Button></Link>
              {!n.read && <Button variant="outline" size="sm" onClick={() => markRead([n.key])} disabled={busy} data-testid={`notif-read-${n.key}`}>Citit</Button>}
            </div>
          </div>; })}
      </div>}
  </div>;
}
